import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Typography,
  Button,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import SuccessPopup from "./SuccessPopup";
import AddIcon from "@mui/icons-material/Add";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const RougingPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [dateOfPlanting, setDateOfPlanting] = useState(null);
  const [comment, setComment] = useState("");
  const { showAlert } = useAlert();
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const [rouging, setRouging] = useState([
    {
      date_of_rouging: "",
      before_rouging: { mixing_percentage: "", virus_percentage: "" },
      after_rouging: { mixing_percentage: "", virus_percentage: "" },
    },
  ]);
  const [errors, setErrors] = useState({
    dateOfPlanting: "",
  });
  let farmerPlotId = getFarmerPlotId();

  const fetchData = async () => {
    try {
      const response = await instance(
        `plotengagement/get-rouging?farmer_plot=${farmerPlotId}`
      );
      let data = response.data;
      if (data.data.length > 0) {
        setExistingData(data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  useEffect(() => {
    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);

  useEffect(() => {
    if (existingData) {
      setDateOfPlanting(new Date(existingData.planting_date));
      setComment(existingData.comments || "");
      // Update rouging data
      const updatedRouging = existingData.rouging.map((entry) => ({
        date_of_rouging: new Date(entry.date_of_rouging),
        before_rouging: {
          mixing_percentage: entry.before_rouging.mixing_percentage,
          virus_percentage: entry.before_rouging.virus_percentage,
        },
        after_rouging: {
          mixing_percentage: entry.after_rouging.mixing_percentage,
          virus_percentage: entry.after_rouging.virus_percentage,
        },
      }));
      setRouging(updatedRouging); // Populate rouging state
    }
  }, [existingData]);

  const handleDateChange = (date) => {
    setDateOfPlanting(date);
    setErrors((prevErrors) => ({
      ...prevErrors,
      dateOfPlanting: "",
    }));
  };

  const handleRougingDateChange = (index, date) => {
    const updatedRouging = [...rouging];
    updatedRouging[index].date_of_rouging = date;
    setRouging(updatedRouging);
    setErrors((prevErrors) => ({
      ...prevErrors,
      dateOfRouging: "",
    }));
  };
  const handleRougingChange = (index, field, value) => {
    setRouging((prevRouging) => {
      const newRouging = [...prevRouging];
      if (field.includes("before_rouging") || field.includes("after_rouging")) {
        const [section, key] = field.split(".");
        newRouging[index][section][key] = value;
      } else {
        newRouging[index][field] = value;
      }
      return newRouging;
    });
  };

  const addRougingEntry = () => {
    setRouging([
      ...rouging,
      {
        date_of_rouging: "",
        before_rouging: { mixing_percentage: "", virus_percentage: "" },
        after_rouging: { mixing_percentage: "", virus_percentage: "" },
      },
    ]);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const validateForm = () => {
    let formValid = true;
    let updatedErrors = {};

    if (!dateOfPlanting) {
      updatedErrors = {
        ...updatedErrors,
        dateOfPlanting: t("Please select a date."),
      };
      formValid = false;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      ...updatedErrors,
    }));

    return formValid;
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleSubmit = () => {
    const isFormValid = validateForm();
    if (isFormValid) {
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      const formData = {
        planting_date: dateOfPlanting ? formatDate(dateOfPlanting) : null,
        rouging: rouging.map((entry) => ({
          date_of_rouging: entry.date_of_rouging
            ? formatDate(entry.date_of_rouging)
            : null,
          before_rouging: {
            mixing_percentage: entry.before_rouging.mixing_percentage,
            virus_percentage: entry.before_rouging.virus_percentage,
          },
          after_rouging: {
            mixing_percentage: entry.after_rouging.mixing_percentage,
            virus_percentage: entry.after_rouging.virus_percentage,
          },
        })),
        comments: comment,
      };
      if (isEditMode) {
        formData.id = existingData.id;
      } else {
        formData.farmer_plot = farmerPlotId;
      }
      const apiUrl = isEditMode
        ? "plotengagement/update-rouging"
        : "plotengagement/create-rouging";
      instance
        .post(apiUrl, formData)
        .then((res) => {
          if (res?.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("Error creating/updating rouging", err);
          showAlert("error", "Some Error Occured");
        });
    }
  };
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 40%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Rouging")}
              </DialogTitle>
            </Grid>
            {existingData !== null ? (
              <Grid item xs={1} style={{ marginTop: "10px" }}>
                <img src={EditIcon} onClick={handleEditClick} alt="edit" />
              </Grid>
            ) : null}
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>

            <Grid container style={{ marginTop: "30px" }}>
              <Grid item xs={7} style={{ marginTop: "20px" }}>
                <InputLabel style={{ fontSize: "12px" }}>
                  {t("Date of Planting")}
                </InputLabel>
              </Grid>

              <Grid item xs={5}>
                <TextField
                  placeholder="DD/MM/YYYY"
                  disabled={!isEditMode && existingData !== null}
                  fullWidth
                  value={
                    dateOfPlanting ? dateOfPlanting.toLocaleDateString() : ""
                  }
                  helperText={errors.dateOfPlanting}
                  error={!!errors.dateOfPlanting}
                  InputProps={{
                    sx: {
                      height: "40px",
                      borderRadius: "5px",
                      marginTop: "10px",
                      background: "#FFF",
                      border: "1px solid #C7BDBD",
                    },
                    readOnly: true,
                    onClick: () =>
                      document.getElementById("date-picker").click(),
                  }}
                />
              </Grid>
              <DatePicker
                id="date-picker"
                dateFormat="dd/MM/yyyy"
                selected={dateOfPlanting}
                onChange={handleDateChange}
                className={classes.datepicker}
              />

              {rouging.map((entry, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  style={{
                    padding: "20px",
                    margin: "10px 0",
                  }}
                >
                  <>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        xs={7}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <InputLabel style={{ fontSize: "12px" }}>
                          {t("Date of Rouging")}
                        </InputLabel>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          placeholder="DD/MM/YYYY"
                          disabled={!isEditMode && existingData !== null}
                          fullWidth
                          value={
                            entry.date_of_rouging
                              ? entry.date_of_rouging.toLocaleDateString()
                              : ""
                          }
                          onClick={() =>
                            document
                              .getElementById(`rouging-date-picker-${index}`)
                              .click()
                          }
                          helperText={errors.dateOfRouging}
                          error={!!errors.dateOfRouging}
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px",
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                            readOnly: true,
                          }}
                        />
                      </Grid>
                      <DatePicker
                        id={`rouging-date-picker-${index}`}
                        selected={entry.date_of_rouging}
                        onChange={(date) =>
                          handleRougingDateChange(index, date)
                        }
                        className={classes.datepicker}
                      />
                    </Grid>

                    {/* Before Percentages */}
                    <Grid container spacing={2} style={{ marginTop: "20px" }}>
                      <Grid item xs={12}>
                        <Typography variant="h6" className={classes.taxtTitle}>
                          {t("Before Rouging")}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={7}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <InputLabel className={classes.taxtLabel}>
                          {t("Mixing Percentage")}
                        </InputLabel>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          placeholder="%"
                          fullWidth
                          value={entry.before_rouging.mixing_percentage}
                          onChange={(e) =>
                            handleRougingChange(
                              index,
                              "before_rouging.mixing_percentage",
                              e.target.value
                            )
                          }
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px",
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={7}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <InputLabel className={classes.taxtLabel}>
                          {t("Virus Percentage")}
                        </InputLabel>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          placeholder="%"
                          fullWidth
                          value={entry.before_rouging.virus_percentage}
                          onChange={(e) =>
                            handleRougingChange(
                              index,
                              "before_rouging.virus_percentage",
                              e.target.value
                            )
                          }
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px",
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* After Percentages */}
                    <Grid container spacing={2} style={{ marginTop: "20px" }}>
                      <Grid item xs={12}>
                        <Typography variant="h6" className={classes.taxtTitle}>
                          {t("After Rouging")}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={7}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <InputLabel className={classes.taxtLabel}>
                          {t("Mixing Percentage")}
                        </InputLabel>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          placeholder="%"
                          fullWidth
                          value={entry.after_rouging.mixing_percentage}
                          onChange={(e) =>
                            handleRougingChange(
                              index,
                              "after_rouging.mixing_percentage",
                              e.target.value
                            )
                          }
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px",
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={7}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <InputLabel className={classes.taxtLabel}>
                          {t("Virus Percentage")}
                        </InputLabel>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          placeholder="%"
                          fullWidth
                          value={entry.after_rouging.virus_percentage}
                          onChange={(e) =>
                            handleRougingChange(
                              index,
                              "after_rouging.virus_percentage",
                              e.target.value
                            )
                          }
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px",
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </>
                </Paper>
              ))}
              <Grid container>
                <Grid item xs={8}></Grid>
                <Grid item xs={4}>
                  <Button
                    onClick={addRougingEntry}
                    style={{
                      background:
                        !isEditMode && existingData !== null
                          ? "rgba(111, 163, 78, 0.5)"
                          : "#6FA34E",
                      border: "#538434",
                      borderRadius: "10px",
                      color: "#FFF",
                      textTransform: "none",
                      marginTop: "10px",
                    }}
                    disabled={!isEditMode && existingData !== null}
                  >
                    <AddIcon style={{ marginRight: "5px", fontSize: "15px" }} />
                    {t("Add More")}
                  </Button>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: "grid", padding: "10px" }}>
                <InputLabel>{t("Comment(Optional)")}</InputLabel>
                <TextField
                  multiline
                  rows={1}
                  value={comment}
                  onChange={handleCommentChange}
                  disabled={!isEditMode && existingData !== null}
                />
              </Grid>

              <div
                style={{
                  display: "flex",
                  margin: "auto",
                  justifyContent: "center",
                }}
              >
                {existingData && isEditMode ? (
                  <div style={{ width: "140px" }}>
                    <CustomButton onClick={handleSubmit} hideIcon>
                      {t("Update")}
                    </CustomButton>
                  </div>
                ) : !existingData && !isEditMode ? (
                  <div
                    style={{
                      display: "flex",
                      margin: "auto",
                      justifyContent: "center",
                      width: "140px",
                    }}
                  >
                    <CustomButton
                      buttonText={t("submit")}
                      handleOnChange={handleSubmit}
                      hideIcon
                    />
                  </div>
                ) : null}
              </div>
            </Grid>
          </Grid>
        </div>
      </Dialog>

      {showSuccessPopup && (
        <SuccessPopup
          message={t("Operation successful!")}
          onClose={() => setShowSuccessPopup(false)}
        />
      )}
    </div>
  );
};

export default RougingPopup;
